import { useEffect, useState } from "react";
import { getAuthUser } from "@/modules/safecheck/auth";

/**
 * Logo / photo de profil du compte connecté.
 * L'API n'a pas de route pour l'image d'un compte : elle est réduite puis conservée dans ce navigateur.
 */

const STORAGE_PREFIX = "safecheck-profile-photo:";
const CHANGE_EVENT = "safecheck:profile-photo";
/** Côté du carré enregistré, en pixels. */
const OUTPUT_SIZE = 256;
export const PROFILE_PHOTO_MAX_BYTES = 2 * 1024 * 1024;
export const PROFILE_PHOTO_ACCEPT = "image/png,image/jpeg,image/webp,image/svg+xml";

function storageKey(): string | null {
  const user = getAuthUser();
  return user ? `${STORAGE_PREFIX}${user.id}` : null;
}

export function getProfilePhoto(): string | null {
  const key = storageKey();
  if (!key) return null;
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function notify(): void {
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

export function removeProfilePhoto(): void {
  const key = storageKey();
  if (!key) return;
  try {
    localStorage.removeItem(key);
  } catch {
    /* stockage indisponible : rien à retirer */
  }
  notify();
}

function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Image illisible."));
    };
    image.src = url;
  });
}

/**
 * Vérifie le fichier, le centre dans un carré de 256 px (logo entier, fond transparent conservé) et l'enregistre.
 * Renvoie l'image enregistrée, ou lève une erreur au message lisible.
 */
export async function saveProfilePhoto(file: File): Promise<string> {
  if (!PROFILE_PHOTO_ACCEPT.split(",").includes(file.type)) {
    throw new Error("Format non pris en charge : PNG, JPG, WebP ou SVG.");
  }
  if (file.size > PROFILE_PHOTO_MAX_BYTES) {
    throw new Error("Fichier trop lourd : 2 Mo maximum.");
  }
  const key = storageKey();
  if (!key) throw new Error("Connectez-vous pour enregistrer un logo.");

  const image = await loadImage(file);
  const canvas = document.createElement("canvas");
  canvas.width = OUTPUT_SIZE;
  canvas.height = OUTPUT_SIZE;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Traitement de l'image impossible.");

  // Le logo entier tient dans le carré, sans être rogné.
  const width = image.naturalWidth || OUTPUT_SIZE;
  const height = image.naturalHeight || OUTPUT_SIZE;
  const scale = Math.min(OUTPUT_SIZE / width, OUTPUT_SIZE / height);
  const drawWidth = width * scale;
  const drawHeight = height * scale;
  context.drawImage(image, (OUTPUT_SIZE - drawWidth) / 2, (OUTPUT_SIZE - drawHeight) / 2, drawWidth, drawHeight);

  const dataUrl = canvas.toDataURL("image/png");
  try {
    localStorage.setItem(key, dataUrl);
  } catch {
    throw new Error("Enregistrement impossible dans ce navigateur.");
  }
  notify();
  return dataUrl;
}

/** Logo du compte connecté, mis à jour dès qu'il change (dans cet onglet comme dans un autre). */
export function useProfilePhoto(): string | null {
  const [photo, setPhoto] = useState<string | null>(() => getProfilePhoto());

  useEffect(() => {
    const refresh = () => setPhoto(getProfilePhoto());
    window.addEventListener(CHANGE_EVENT, refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener(CHANGE_EVENT, refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);

  return photo;
}
